/**
 * AlertFilter.js
 * 告警消息过滤工具类
 * 用于过滤包含特定关键字的告警消息
 */
class AlertFilter {
  constructor() {
    // 定义需要过滤的关键字
    this._keywords = [
      '偏航',
      '超高', 
      '超速',
      '滞留',
      '黑名单',
      '穿越',
      '进入',
      '离开'
    ];
    
    // 定义关键字的同义词或变体
    this._keywordVariants = {
      '偏航': ['偏航', '偏离航线', '航线偏离', '航向偏离', '偏离航向'],
      '超高': ['超高', '高度超限', '超高度', '高度超标', '超限高度'],
      '超速': ['超速', '速度超限', '超速度', '速度超标', '超限速度'],
      '滞留': ['滞留', '停留超时', '超时停留', '停留时间过长', '长时间停留'],
      '黑名单': ['黑名单', '黑名单船舶', '黑名单船只', '黑名单船'],
      '进入': ['进入', '驶入', '驶入码头', '驶入港口', '驶入港口码头'],
      '离开': ['离开', '驶离', '驶离码头', '驶离港口码头', '驶离港口码头'],
      '穿越': ['穿越', '穿越桥梁', '桥梁穿越', '桥梁穿越桥梁', '桥梁穿越桥梁']
    };
    
    // 是否启用过滤
    this._enabled = true;
    
    // 是否启用严格模式（必须完全匹配关键字）
    this._strictMode = false;
    
    // 是否启用模糊匹配（支持部分匹配）
    this._fuzzyMatch = true;
  }

  /**
   * 检查告警消息是否包含需要过滤的关键字
   * @param {string} message - 告警消息文本
   * @returns {boolean} 是否应该显示和播报
   */
  shouldDisplay(message) {
    if (!message) {
      return false;
    }

    if (!this._enabled) {
      return true; // 如果过滤未启用或消息为空，默认显示
    }

    // 转换为字符串并转为小写进行匹配
    const messageText = String(message).toLowerCase();
    
    // 检查是否包含任何关键字
    for (const keyword of this._keywords) {
      if (this._containsKeyword(messageText, keyword)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * 检查消息是否包含指定关键字（支持同义词和变体）
   * @param {string} messageText - 消息文本（已转为小写）
   * @param {string} keyword - 关键字
   * @returns {boolean} 是否包含关键字
   * @private
   */
  _containsKeyword(messageText, keyword) {
    const variants = this._keywordVariants[keyword] || [keyword];
    
    for (const variant of variants) {
      const lowerVariant = variant.toLowerCase();
      
      if (this._strictMode) {
        // 严格模式：必须完全匹配
        if (messageText === lowerVariant) {
          return true;
        }
      } else if (this._fuzzyMatch) {
        // 模糊匹配：包含关键字即可
        if (messageText.includes(lowerVariant)) {
          return true;
        }
      } else {
        // 普通匹配：精确包含
        if (messageText.includes(lowerVariant)) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * 获取消息中包含的关键字列表
   * @param {string} message - 告警消息文本
   * @returns {Array<string>} 包含的关键字列表
   */
  getMatchedKeywords(message) {
    if (!message) {
      return [];
    }

    const messageText = String(message).toLowerCase();
    const matchedKeywords = [];
    
    for (const keyword of this._keywords) {
      if (this._containsKeyword(messageText, keyword)) {
        matchedKeywords.push(keyword);
      }
    }
    
    return matchedKeywords;
  }

  /**
   * 添加新的过滤关键字
   * @param {string} keyword - 新关键字
   * @param {Array<string>} variants - 关键字的变体（可选）
   */
  addKeyword(keyword, variants = []) {
    if (keyword && !this._keywords.includes(keyword)) {
      this._keywords.push(keyword);
      this._keywordVariants[keyword] = variants;
      console.log(`AlertFilter: 已添加新关键字 "${keyword}"`);
    }
  }

  /**
   * 移除过滤关键字
   * @param {string} keyword - 要移除的关键字
   */
  removeKeyword(keyword) {
    const index = this._keywords.indexOf(keyword);
    if (index > -1) {
      this._keywords.splice(index, 1);
      delete this._keywordVariants[keyword];
      console.log(`AlertFilter: 已移除关键字 "${keyword}"`);
    }
  }

  /**
   * 设置关键字变体
   * @param {string} keyword - 关键字
   * @param {Array<string>} variants - 变体列表
   */
  setKeywordVariants(keyword, variants) {
    if (this._keywords.includes(keyword)) {
      this._keywordVariants[keyword] = variants || [];
      console.log(`AlertFilter: 已更新关键字 "${keyword}" 的变体`);
    }
  }

  /**
   * 获取所有过滤关键字
   * @returns {Array<string>} 关键字列表
   */
  getKeywords() {
    return [...this._keywords];
  }

  /**
   * 获取关键字的变体
   * @param {string} keyword - 关键字
   * @returns {Array<string>} 变体列表
   */
  getKeywordVariants(keyword) {
    return [...(this._keywordVariants[keyword] || [])];
  }

  /**
   * 启用或禁用过滤
   * @param {boolean} enabled - 是否启用过滤
   */
  setEnabled(enabled) {
    this._enabled = enabled;
    console.log(`AlertFilter: 过滤功能已${enabled ? '启用' : '禁用'}`);
  }

  /**
   * 设置严格模式
   * @param {boolean} strict - 是否启用严格模式
   */
  setStrictMode(strict) {
    this._strictMode = strict;
    console.log(`AlertFilter: 严格模式已${strict ? '启用' : '禁用'}`);
  }

  /**
   * 设置模糊匹配
   * @param {boolean} fuzzy - 是否启用模糊匹配
   */
  setFuzzyMatch(fuzzy) {
    this._fuzzyMatch = fuzzy;
    console.log(`AlertFilter: 模糊匹配已${fuzzy ? '启用' : '禁用'}`);
  }

  /**
   * 获取过滤配置信息
   * @returns {Object} 配置信息
   */
  getConfig() {
    return {
      enabled: this._enabled,
      strictMode: this._strictMode,
      fuzzyMatch: this._fuzzyMatch,
      keywords: this.getKeywords(),
      keywordVariants: { ...this._keywordVariants }
    };
  }

  /**
   * 重置为默认配置
   */
  resetToDefault() {
    this._keywords = [
      '偏航',
      '超高', 
      '超速',
      '滞留',
      '黑名单',
      '穿越',
      '进入',
      '离开'
    ];
    
    this._keywordVariants = {
      '偏航': ['偏航', '偏离航线', '航线偏离', '航向偏离', '偏离航向'],
      '超高': ['超高', '高度超限', '超高度', '高度超标', '超限高度'],
      '超速': ['超速', '速度超限', '超速度', '速度超标', '超限速度'],
      '滞留': ['滞留', '停留超时', '超时停留', '停留时间过长', '长时间停留'],
      '黑名单': ['黑名单', '黑名单船舶', '黑名单船只', '黑名单船'],
      '穿越': ['穿越', '穿越桥梁', '桥梁穿越', '桥梁穿越桥梁', '桥梁穿越桥梁'],
      '进入': ['进入', '驶入', '驶入码头', '驶入港口', '驶入港口码头'],
      '离开': ['离开', '驶离', '驶离码头', '驶离港口码头', '驶离港口码头'],
      '穿越': ['穿越', '穿越桥梁', '桥梁穿越', '桥梁穿越桥梁', '桥梁穿越桥梁']
    };
    
    this._enabled = true;
    this._strictMode = false;
    this._fuzzyMatch = true;
    
    console.log('AlertFilter: 已重置为默认配置');
  }

  /**
   * 测试消息是否匹配过滤规则
   * @param {string} message - 测试消息
   * @returns {Object} 测试结果
   */
  testMessage(message) {
    const shouldDisplay = this.shouldDisplay(message);
    const matchedKeywords = this.getMatchedKeywords(message);
    
    return {
      message,
      shouldDisplay,
      matchedKeywords,
      config: this.getConfig()
    };
  }
}

// 创建并导出单例实例
const alertFilter = new AlertFilter();

export { alertFilter, AlertFilter };
export default alertFilter;
